/**
 * chat-history.js — Chat transcript persistence.
 *
 * PURPOSE:
 *   session.js persists the session_id so the conversation handle survives a
 *   reload; this module persists the *messages* themselves so the rendered
 *   transcript survives too. Without it, a refresh kept the session but lost
 *   every bubble and MessageList re-seeded the welcome.
 *
 * STORE:
 *   localStorage, keyed per tenant AND session: `ws-chat-msgs:<tenant>:<session>`.
 *   Tying the key to the session id means ending the conversation (new session)
 *   naturally starts a clean transcript, and stale histories never bleed across
 *   sessions. Capped at CAP messages (most recent kept).
 *
 * WHAT IS PERSISTED:
 *   The structured messages array from state.js ({id,role,kind,data,meta,
 *   timestamp}). The transient typing bubble is NOT a message (it lives in
 *   ws-chat-typing events, not the array) so it is never written.
 *
 * USAGE:
 *   import { loadHistory, clearHistory, attachChatHistoryPersistence } from "./chat-history.js";
 */

import { on, getState, getMessages } from "./state.js";

const PREFIX = "ws-chat-msgs";
const CAP = 50;

const keyFor = (tenantId, sessionId) =>
  `${PREFIX}:${tenantId || "default"}:${sessionId}`;

// Returns the stored transcript for this tenant+session, or [].
export function loadHistory(tenantId, sessionId) {
  if (!sessionId) return [];
  try {
    const raw = localStorage.getItem(keyFor(tenantId, sessionId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Writes the transcript (capped to the most recent CAP). No-op without a
// session id — the key is meaningless until the session exists.
export function saveHistory(tenantId, sessionId, messages) {
  if (!sessionId) return;
  try {
    const capped = messages.length > CAP ? messages.slice(-CAP) : messages;
    localStorage.setItem(keyFor(tenantId, sessionId), JSON.stringify(capped));
  } catch {
    /* storage blocked or over quota — transcript stays in memory for this page */
  }
}

// Removes the stored transcript for a tenant+session. Called when the session
// ends (see session.js endSession) so the next session starts clean.
export function clearHistory(tenantId, sessionId) {
  if (!sessionId) return;
  try {
    localStorage.removeItem(keyFor(tenantId, sessionId));
  } catch {
    /* ignore */
  }
}

// Mirror every transcript mutation to localStorage. Adds/updates are debounced
// so token-by-token streaming doesn't thrash storage; a clear flushes
// immediately so a refresh mid-clear can't restore stale messages. The session
// id is read live at flush time because the session is created lazily (on first
// panel open), after this subscriber is attached at boot.
export function attachChatHistoryPersistence(tenantId) {
  let timer = null;

  const flush = () => {
    timer = null;
    saveHistory(tenantId, getState().session.id, getMessages());
  };
  const schedule = () => {
    if (timer) return;
    timer = setTimeout(flush, 300);
  };

  on("ws-message-added", schedule);
  on("ws-message-updated", schedule);
  on("ws-message-removed", schedule);
  on("ws-messages-cleared", () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    saveHistory(tenantId, getState().session.id, getMessages());
  });
}
